import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { makeStyles, ThemeProvider } from '@material-ui/core/styles'
import { addDays, fromUnixTime, getUnixTime, startOfDay, subMonths } from 'date-fns'

import { DatePicker } from '@material-ui/pickers'
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Typography } from '@material-ui/core'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { LifecycleTooltip } from './LifecycleTooltip'

import { useApi, useStore } from '../hooks/storeHook'

import { appRoutes } from '../common/appRoutes'
import { datePickerTheme } from '../common/appTheme'
import { valuesOfNumericEnum } from '../common/tsUtils'
import { isUiException } from '../common/requestValidationError'
import { dateFormats, formatDate } from '../common/dateTimeUtility'

import type { AdminMsAppLifecycleChartModel } from '../server/mpsklad_core/Models/AdminMsAppLifecycleChartModel'
import { MoySkladAppLifecycleEventKind } from '../server/mpsklad_core/Entity/Enums/MoySkladAppLifecycleEventKind'
import type { AdminMsAppLifecycleChartOptions } from '../server/mpsklad_core/Models/AdminMsAppLifecycleChartOptions'

const eventKindNames: {
  readonly [eventKind in MoySkladAppLifecycleEventKind]: string
} = {
  [MoySkladAppLifecycleEventKind.Install]: 'Установка',
  [MoySkladAppLifecycleEventKind.Resume]: 'Возобновление',
  [MoySkladAppLifecycleEventKind.TariffChanged]: 'Смена тарифа',
  [MoySkladAppLifecycleEventKind.Autoprolongation]: 'Автопродление',
  [MoySkladAppLifecycleEventKind.Suspend]: 'Приостановка',
  [MoySkladAppLifecycleEventKind.UninstallRequested]: 'Удаление (запрос)',
  [MoySkladAppLifecycleEventKind.UninstallCompleted]: 'Удаление (завершено)'
} as const

const eventKindColors: {
  readonly [eventKind in MoySkladAppLifecycleEventKind]: string
} = {
  [MoySkladAppLifecycleEventKind.Install]: '#1976d2',
  [MoySkladAppLifecycleEventKind.Resume]: '#388e3c',
  [MoySkladAppLifecycleEventKind.TariffChanged]: '#f57c00',
  [MoySkladAppLifecycleEventKind.Autoprolongation]: '#7b1fa2',
  [MoySkladAppLifecycleEventKind.Suspend]: '#c62828',
  [MoySkladAppLifecycleEventKind.UninstallRequested]: '#546e7a',
  [MoySkladAppLifecycleEventKind.UninstallCompleted]: '#455a64'
} as const

const allEventKinds = valuesOfNumericEnum(MoySkladAppLifecycleEventKind)

const formatBucketLabel =
  (unixSec: number, hourly: boolean) =>
    formatDate(fromUnixTime(unixSec), hourly ? 'HH:mm' : 'd MMM')

export const AdminMsAppLifecycleScreen =
  observer(
    () => {
      const {user} = useStore()
      const api = useApi()
      const history = useHistory()
      const classes = useStyles()

      const [fromDate, setFromDate] = useState(() => startOfDay(new Date()))
      const [toDate, setToDate] = useState(() => startOfDay(new Date()))

      const [selectedKinds, setSelectedKinds] = useState<MoySkladAppLifecycleEventKind[]>(() => [...allEventKinds])

      const [chart, setChart] = useState<AdminMsAppLifecycleChartModel | null>(null)
      const [chartPrevious, setChartPrevious] = useState<AdminMsAppLifecycleChartModel | null>(null)
      const [showPreviousMonthOverlay, setShowPreviousMonthOverlay] = useState(true)

      const [loading, setLoading] = useState(false)
      const [loadError, setLoadError] = useState<string | null>(null)

      // TODO: mobx is better at handling this kind of loading pattern, e.g., React can't handle deduped loading.
      const load =
        useCallback(async () => {
            // Can't check vs `loading` - causes either cyclic or unvalidated dependency
            setLoading(true)
            setLoadError(null)

            try {
              if (startOfDay(fromDate).getTime() > startOfDay(toDate).getTime()) {
                setLoadError('Дата «с» не может быть позже даты «по».')
                setChart(null)
                setChartPrevious(null)
                return
              }

              if (selectedKinds.length === 0) {
                setLoadError('Выберите хотя бы один тип события.')
                setChart(null)
                setChartPrevious(null)
                return
              }

              const fromUtcUnixSeconds = getUnixTime(startOfDay(fromDate))
              const toUtcExclusiveUnixSeconds = getUnixTime(addDays(startOfDay(toDate), 1))
              const payload: AdminMsAppLifecycleChartOptions = {fromUtcUnixSeconds, toUtcExclusiveUnixSeconds}

              if (selectedKinds.length < allEventKinds.length) {
                payload.eventKinds = [...selectedKinds].sort((a, b) => a - b)
              }

              if (showPreviousMonthOverlay) {
                setChartPrevious(null)
              }

              setChart(await api.admin.msAppLifecycleChart(payload))

              if (showPreviousMonthOverlay) {
                const prevFromDay = startOfDay(subMonths(fromDate, 1))
                const prevToDay = addDays(startOfDay(subMonths(toDate, 1)), 1)

                const prevPayload: AdminMsAppLifecycleChartOptions = {
                  fromUtcUnixSeconds: getUnixTime(prevFromDay),
                  toUtcExclusiveUnixSeconds: getUnixTime(prevToDay)
                }

                if (selectedKinds.length < allEventKinds.length) {
                  prevPayload.eventKinds = [...selectedKinds].sort((a, b) => a - b)
                }

                try {
                  setChartPrevious(await api.admin.msAppLifecycleChart(prevPayload))
                } catch {
                  setChartPrevious(null)
                }
              } else {
                setChartPrevious(null)
              }
            } catch (e: unknown) {
              const msg = isUiException(e) ? e.response.data.uiMessage : 'Не удалось загрузить статистику'
              setLoadError(msg)
              setChart(null)
              setChartPrevious(null)
            } finally {
              setLoading(false)
            }
          },
          [api, fromDate, toDate, selectedKinds, showPreviousMonthOverlay])

      useEffect(() => {
          load().catch(console.error)
        },
        [load])

      const rechartsRows =
        useMemo(() => {
          if (!chart?.buckets.length) {
            return []
          }

          const primaryBuckets = chart.buckets
          const prevBuckets = chartPrevious?.buckets ?? []
          const isHourly = chart.granularity === 'hour'
          const includePrev = showPreviousMonthOverlay && prevBuckets.length > 0

          return primaryBuckets.map((b, i) => {
            const row: Record<string, string | number> = {
              name: formatBucketLabel(b.bucketStartUtcUnixSeconds, isHourly)
            }

            for (const kind of selectedKinds) {
              row[`k${kind}`] = b.countsByEventKind.find(c => c.eventKind === kind)?.count ?? 0
            }

            if (includePrev) {
              const pb = prevBuckets[i]
              for (const kind of selectedKinds) {
                row[`k${kind}Prev`] = pb ? pb.countsByEventKind.find(c => c.eventKind === kind)?.count ?? 0 : 0
              }
            }

            return row
          })
        }, [chart, chartPrevious, selectedKinds, showPreviousMonthOverlay])

      if (!user?.isSuperAdmin) {
        return null
      }

      const toggleKind =
        (kind: MoySkladAppLifecycleEventKind) => {
          setSelectedKinds(prev => {
            if (prev.includes(kind)) {
              return prev.filter(k => k !== kind)
            }
            return [...prev, kind].sort((a, b) => a - b)
          })
        }

      const setKindsPreset =
        (kinds: readonly MoySkladAppLifecycleEventKind[]) => {
          setSelectedKinds([...kinds].sort((a, b) => a - b))
        }

      const hourly = chart?.granularity === 'hour'
      const buckets = chart?.buckets ?? []

      const showPrevLines =
        showPreviousMonthOverlay && (chartPrevious?.buckets.length ?? 0) > 0

      const prevOverlayMissing =
        showPreviousMonthOverlay &&
        !loading &&
        !!chart &&
        (!chartPrevious || (chartPrevious.buckets.length ?? 0) === 0)

      return (
        <div className="body_wrapper_zero">
          <div className="body_wrapper">
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" marginBottom={2}>
              <h1 className={classes.title}>
                Статистика МС-приложения
              </h1>

              <Box display="flex" flexWrap="wrap" alignItems="center">
                <Button
                  className={classes.navButton}
                  variant="outlined"
                  onClick={() => history.push(appRoutes.AdminUsers)}>
                  Пользователи
                </Button>

                <Button
                  className={classes.navButton}
                  variant="outlined"
                  onClick={() => history.push(appRoutes.Account)}>
                  Аккаунт
                </Button>
              </Box>
            </Box>

            <Typography variant="body2" color="textSecondary" paragraph>
              По умолчанию — текущие локальные сутки и все типы событий. При смене дат или типов график
              перезагружается автоматически; «Обновить» — для того же периода. Группировка: по часам для интервала до
              ~30 ч, иначе по дням. «Прошлый месяц» — на фоне пунктиром те же сутки календарного месяца назад.
            </Typography>

            <Paper className={classes.filterPaper}>
              <Typography variant="subtitle2" gutterBottom>
                Типы событий
              </Typography>

              <Box display="flex" flexWrap="wrap" marginBottom={2}>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.presetButton}
                  onClick={() => setKindsPreset(allEventKinds)}>
                  Все
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  className={classes.presetButton}
                  onClick={() => setKindsPreset([0])}>
                  Установка
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  className={classes.presetButton}
                  onClick={() => setKindsPreset([1])}>
                  Возобновление
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  className={classes.presetButton}
                  onClick={() => setKindsPreset([2, 3])}>
                  Тариф и продление
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  className={classes.presetButton}
                  onClick={() => setKindsPreset([4])}>
                  Приостановка
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  className={classes.presetButton}
                  onClick={() => setKindsPreset([5, 6])}>
                  Удаление
                </Button>
              </Box>

              <Box display="flex" flexWrap="wrap" className={classes.kindCheckboxes}>
                {
                  allEventKinds.map(kind => (
                    <FormControlLabel
                      key={kind}
                      className={classes.kindCheckbox}
                      control={
                        <Checkbox
                          checked={selectedKinds.includes(kind)}
                          onChange={() => toggleKind(kind)}
                          color="default"
                          size="small"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center">
                          <Box className={classes.legendSwatch} style={{backgroundColor: eventKindColors[kind]}}/>
                          <Typography variant="body2">{eventKindNames[kind]}</Typography>
                        </Box>
                      }
                    />
                  ))
                }
              </Box>

              <Box display="flex" flexWrap="wrap" alignItems="flex-end" marginTop={2}>
                <ThemeProvider theme={datePickerTheme}>
                  <Box marginRight={2} marginBottom={1}>
                    <DatePicker
                      label="С даты"
                      format={dateFormats.dateOnly}
                      value={fromDate}
                      onChange={d => d && setFromDate(d)}
                      cancelLabel="Отменить"
                      okLabel="Принять"
                      clearable={false}
                    />
                  </Box>

                  <Box marginRight={2} marginBottom={1}>
                    <DatePicker
                      label="По дату"
                      format={dateFormats.dateOnly}
                      value={toDate}
                      onChange={d => d && setToDate(d)}
                      cancelLabel="Отменить"
                      okLabel="Принять"
                      clearable={false}
                    />
                  </Box>
                </ThemeProvider>

                <Button color="primary" variant="contained" disabled={loading} onClick={load}>
                  Обновить
                </Button>

                {loading && <CircularProgress className={classes.loader} size={28}/>}
              </Box>

              <FormControlLabel
                className={classes.overlayCheckbox}
                control={
                  <Checkbox
                    className={classes.overlayCheckboxControl}
                    checked={showPreviousMonthOverlay}
                    onChange={(_, c) => setShowPreviousMonthOverlay(c)}
                    color="default"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Прошлый месяц</Typography>}
              />
            </Paper>

            {
              loadError &&
              <Typography color="error" paragraph>
                {loadError}
              </Typography>
            }

            {
              chart &&
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Интервал: {formatDate(startOfDay(fromDate), 'd MMMM yyyy')}
                  {' — '}
                  {formatDate(startOfDay(toDate), 'd MMMM yyyy')}
                  {' · '}
                  группировка: {hourly ? 'час' : 'день'}
                  {selectedKinds.length < allEventKinds.length &&
                   <>
                     {' · '}
                     типы: {[...selectedKinds].sort((a, b) => a - b).map(k => eventKindNames[k]).join(', ')}
                   </>}
                </Typography>

                {
                  prevOverlayMissing &&
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    «Прошлый месяц» недоступен (ошибка загрузки или нет данных за тот период).
                  </Typography>
                }

                <Paper className={classes.chartPaper}>
                  {
                    buckets.length === 0
                    ? <Typography color="textSecondary">Нет событий за выбранный период.</Typography>
                    : <Box className={classes.rechartsWrap}>
                      <ResponsiveContainer width="100%" height={440}>
                        <LineChart
                          data={rechartsRows}
                          margin={{top: 12, right: 20, left: 4, bottom: hourly ? 56 : 36}}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false}/>

                          <XAxis
                            dataKey="name"
                            interval={hourly ? 'preserveStartEnd' : 0}
                            angle={hourly ? -35 : 0}
                            textAnchor={hourly ? 'end' : 'middle'}
                            height={hourly ? 52 : 28}
                            tick={{fontSize: 11}}
                          />

                          <YAxis allowDecimals={false} width={40} tick={{fontSize: 11}}/>

                          <Tooltip content={LifecycleTooltip}/>

                          <Legend wrapperStyle={{paddingTop: 8}}/>

                          {
                            showPrevLines &&
                            [...selectedKinds].sort((a, b) => a - b).map(kind =>
                              <Line
                                key={`prev-${kind}`}
                                type="monotone"
                                dataKey={`k${kind}Prev`}
                                name={`${eventKindNames[kind]} (прошлый месяц)`}
                                stroke={eventKindColors[kind]}
                                strokeOpacity={0.5}
                                strokeWidth={1.5}
                                strokeDasharray="5 4"
                                dot={false}
                                activeDot={{r: 3}}
                                isAnimationActive={false}
                              />)
                          }

                          {
                            [...selectedKinds].sort((a, b) => a - b).map(kind =>
                              <Line
                                key={kind}
                                type="monotone"
                                dataKey={`k${kind}`}
                                name={eventKindNames[kind]}
                                stroke={eventKindColors[kind]}
                                strokeWidth={2}
                                dot={{r: 2}}
                                isAnimationActive={false}
                              />)
                          }
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  }
                </Paper>

                <Box marginTop={2} display="flex" flexWrap="wrap">
                  {
                    chart.totalsByEventKind.map(row =>
                      <Box key={row.eventKind} display="flex" alignItems="center" marginRight={2} marginBottom={1}>
                        <Box
                          className={classes.legendSwatch}
                          style={{backgroundColor: eventKindColors[row.eventKind]}}/>

                        <Typography variant="body2">
                          {eventKindNames[row.eventKind]}: <strong>{row.count}</strong>
                        </Typography>
                      </Box>)
                  }
                </Box>
              </>
            }
          </div>
        </div>
      )
    })

const useStyles =
  makeStyles(
    ({spacing}) => ({
      title: {
        margin: 0,
        marginRight: spacing(2)
      },
      navButton: {
        marginRight: spacing(1),
        marginBottom: spacing(1)
      },
      filterPaper: {
        padding: spacing(2),
        marginBottom: spacing(2)
      },
      presetButton: {
        marginRight: spacing(1),
        marginBottom: spacing(1)
      },
      kindCheckboxes: {
        marginTop: spacing(0.5)
      },
      kindCheckbox: {
        marginRight: spacing(2),
        marginBottom: spacing(0.5)
      },
      overlayCheckbox: {
        marginTop: spacing(1),
        marginLeft: 0,
        marginRight: 0,
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        // сброс дефолта MUI (-11px), из‑за него чекбокс «уезжает» относительно полей дат
        '&.MuiFormControlLabel-root': {
          marginLeft: 0
        }
      },
      overlayCheckboxControl: {
        // у Checkbox по умолчанию padding: 9px — ломает выравнивание с DatePicker
        padding: spacing(0.5)
      },
      chartPaper: {
        padding: spacing(2),
        overflowX: 'auto'
      },
      rechartsWrap: {
        width: '100%',
        minHeight: 440
      },
      legendSwatch: {
        width: 12,
        height: 12,
        borderRadius: 2,
        marginRight: spacing(0.75),
        flexShrink: 0
      },
      loader: {
        marginLeft: spacing(2)
      }
    }))